import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ScrollText, AlertTriangle, AlertOctagon, Ban } from "lucide-react";

const FAULT_TYPES = [
  {
    id: "leve",
    label: "Falta LEVE",
    color: "bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)]",
    icon: AlertTriangle,
    desc: "Errores pequeños que no comprometen la seguridad, pero denotan falta de práctica o pericia. Se acumulan hasta un límite antes de suspender.",
  },
  {
    id: "deficiente",
    label: "Falta DEFICIENTE",
    color: "bg-orange-500 text-white",
    icon: AlertOctagon,
    desc: "Errores importantes que ponen en compromiso al alumno o a terceros aunque no llegan a ser peligrosos. Pesan mucho: 2 hacen suspender directamente.",
  },
  {
    id: "eliminatoria",
    label: "Falta ELIMINATORIA",
    color: "bg-[var(--faro-red)] text-white",
    icon: Ban,
    desc: "Errores graves que suponen un riesgo real (invadir carril contrario, no obedecer señales de prioridad, superar la velocidad de forma peligrosa, etc.). Con 1 sola se suspende.",
  },
];

const EXAMS = [
  {
    id: "destreza",
    title: "Examen de destreza (maniobras en pista)",
    subtitle: "Aparcamientos, cambio de sentido, maniobras controladas",
    fails: [
      "1 falta eliminatoria",
      "2 faltas deficientes",
      "1 falta deficiente y 2 faltas leves",
      "4 faltas leves",
    ],
  },
  {
    id: "circulacion",
    title: "Examen de circulación (en tráfico real)",
    subtitle: "Conducción en ciudad y carretera con el examinador de la DGT",
    fails: [
      "1 falta eliminatoria",
      "2 faltas deficientes",
      "1 falta deficiente y 5 faltas leves",
      "10 faltas leves",
    ],
  },
];

function CriteriaBody() {
  return (
    <div className="px-5 sm:px-6 pb-6 pt-1 space-y-6">
              <div className="rounded-xl bg-[var(--faro-yellow)]/25 border-l-4 border-[var(--faro-yellow)] p-4" data-testid="dgt-exam-duration">
                <div className="text-xs uppercase tracking-[0.18em] font-black text-[var(--faro-blue-deep)] mb-1">Duración del examen</div>
                <ul className="text-sm text-[var(--faro-text)] space-y-1">
                  <li>• Todos los permisos: <b>mínimo 30 minutos</b>.</li>
                  <li>• Camión, autobús y trailer (C, D, C+E, D+E): <b>mínimo 45 minutos</b>.</li>
                  <li>• Los <b>10 primeros minutos son de conducción autónoma</b>: el alumno decide la ruta, aunque el examinador puede introducir ligeras correcciones.</li>
                </ul>
                <a href="/criterios-dgt" data-testid="dgt-selector-link" className="btn-pill btn-primary text-xs mt-4 inline-flex">
                  Ver todas las faltas por permiso y prueba →
                </a>
              </div>

              <p className="text-sm text-[var(--faro-text-soft)] leading-relaxed">
                Los exámenes prácticos de la DGT (basados en el <b>Real Decreto 818/2009</b>) se puntúan sumando faltas. Existen tres tipos, y suspendes cuando alcanzas cualquiera de las combinaciones que verás abajo.
              </p>

              {/* Fault types */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {FAULT_TYPES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.id} className="rounded-xl border border-[rgba(30,42,143,0.1)] p-4 bg-[var(--faro-bg)]" data-testid={`dgt-fault-${f.id}`}>
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${f.color}`}>
                        <Icon size={12} /> {f.label}
                      </span>
                      <p className="mt-3 text-xs leading-relaxed text-[var(--faro-text-soft)]">{f.desc}</p>
                    </div>
                  );
                })}
              </div>

              {/* Exam tables */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {EXAMS.map((ex) => (
                  <div key={ex.id} className="rounded-xl border border-[rgba(30,42,143,0.12)] overflow-hidden" data-testid={`dgt-exam-${ex.id}`}>
                    <div className="bg-[var(--faro-blue)] text-white px-4 py-3">
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[var(--faro-yellow)]">
                        Cuándo suspendes
                      </div>
                      <div className="font-display font-black text-base leading-tight">{ex.title}</div>
                      <div className="text-xs text-white/80 mt-0.5">{ex.subtitle}</div>
                    </div>
                    <ul className="divide-y divide-[rgba(30,42,143,0.08)] bg-white">
                      {ex.fails.map((f) => (
                        <li key={f} className="px-4 py-3 flex items-center gap-2 text-sm text-[var(--faro-text)]">
                          <Ban size={14} className="text-[var(--faro-red)] shrink-0" />
                          <span><b>NO APTO</b> con {f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

      <p className="text-xs text-[var(--faro-text-soft)] leading-relaxed">
        📚 Fuente: <a href="https://www.boe.es/eli/es/rd/2009/05/08/818" target="_blank" rel="noopener noreferrer" className="text-[var(--faro-red)] font-semibold hover:underline">Real Decreto 818/2009 (BOE)</a>. Consulta a tu profesor Faro para el desglose completo de cada falta por permiso y tipo de vehículo.
      </p>
    </div>
  );
}

export default function DgtCriteria() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-6 rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white overflow-hidden shadow-md shadow-[rgba(30,42,143,0.05)]" data-testid="dgt-criteria">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="dgt-criteria-toggle"
        className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left hover:bg-[var(--faro-bg)] transition-colors"
      >
        <span className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[var(--faro-blue)] text-white flex items-center justify-center shrink-0">
            <ScrollText size={18} />
          </span>
          <span>
            <span className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--faro-red)]">
              Criterios oficiales DGT
            </span>
            <span className="block font-display font-black text-base sm:text-lg text-[var(--faro-text)]">
              ¿Cómo se califica el examen práctico?
            </span>
          </span>
        </span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown size={20} className="text-[var(--faro-blue)]" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <CriteriaBody />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
